import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  debounce,
  getDayjsObject,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  hasCheckSecurity,
} from "@/util/common_function";
import { Form } from "antd";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";
import ApprovalReadOnlyValue from "./ApprovalReadOnlyValue";

const Administrator = forwardRef(({
  data,
  updateForm = null,
  parentFieldEnable = false,
  fromkey = "",
  startingPoint="",
}, ref) => {
  const { userDetail } = useSelector((s) => s?.userDetail);
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
  const [form] = Form.useForm();
  const [administratorData, setAdministratorData] = useState(null);

  const status = Form.useWatch(
    isNonEmptyVariable(fromkey)
      ? [fromkey, 0, "administratorStatus"]
      : "administratorStatus",
    form,
  );

  // Moved above the data-watching effect so it's available there
  const allOptions = getOptionsFromRefList("JailIncidentsStatusAdministrator");

  // Status starts blank on a fresh record and stays whatever the user
  // actually picks - it's no longer defaulted to Pending here. Save
  // Changes (JailIncidentDetail.jsx's saveJailIncident -> withDefaultStatus)
  // is what fills in "Pending" for any status still unset at save time,
  // without touching one the user already set to something else.
  useEffect(() => {
    if (isNonEmptyVariable(data)) {
      setAdministratorData({
        ...data,
        administratorStaff: isNonEmptyVariable(data?.administratorStaffId)
          ? getRefListNameByID("", data?.administratorStaffId, true, true)
          : "",
        administratorDateTime: getDayjsObject(
          data?.administratorDate,
          data?.administratorTime,
        ),
      });
    } else {
      setAdministratorData(null);
    }
  }, [data]);

  useEffect(() => {
    if (isNonEmptyVariable(administratorData)) {
      if (isNonEmptyVariable(fromkey)) {
        form.setFieldsValue({
          [fromkey]: [administratorData],
        });
      } else {
        form.setFieldsValue(administratorData);
      }
    }
  }, [administratorData]);

  // Shapes whatever is currently in the (possibly nested, via fromkey) form
  // fields into the administratorDate/administratorTime payload shape
  // callers expect. Reads live field state - no validation gate - so it
  // always reflects "the selected one inside the form" at the instant it's
  // called.
  const buildAdministratorData = () => {
    const formValues = form.getFieldsValue(true);
    let values = formValues;
    if (isNonEmptyVariable(fromkey)) {
      values = formValues?.[fromkey]?.[0];
    }
    const _administratorDateTime = values?.administratorDateTime
      ? dayjs(values?.administratorDateTime)?.format("MM/DD/YYYY HH:mm")
      : null;

    // Only the fields Administrator actually owns. `values` can also carry
    // unrelated fields (e.g. supervisorStatus) dragged in by the [data]
    // effect above spreading the WHOLE inmate row into this form's store -
    // returning those here would let a stale copy clobber Supervisor's own
    // (correct) value when the two are merged in getApprovalValues().
    return {
      administratorStaffId: values?.administratorStaffId,
      administratorStatus: values?.administratorStatus,
      administratorRejectReason: values?.administratorRejectReason,
      administratorDate: _administratorDateTime?.split(" ")?.[0],
      administratorTime: _administratorDateTime?.split(" ")?.[1],
    };
  };

  // Lets a parent (e.g. the Involved Inmate modal's Save) pull the current
  // Administrator selection synchronously at submit time, instead of
  // relying solely on the debounced onFieldsChange -> updateForm round-trip
  // below, which can lose the selection if Save is clicked before it fires.
  useImperativeHandle(ref, () => ({
    getValues: buildAdministratorData,
  }));

  const dataModify = debounce(() => {
    updateForm(buildAdministratorData());
  }, 800);

  const changeStatus = (e) => {
    if (!isNonEmptyVariable(e)) return;
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "administratorDateTime"]
        : "administratorDateTime",
      dayjs(),
    );
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "administratorStaffId"]
        : "administratorStaffId",
      userDetail?.staffId,
    );
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "administratorRejectReason"]
        : "administratorRejectReason",
      "",
    );

    // Push the status change to the parent immediately instead of relying
    // solely on the 800ms debounced onFieldsChange -> dataModify round-trip
    // below. Save Changes reads jailIncident straight out of redux to decide
    // Open/Closed - if the user changes Status and clicks Save before the
    // debounce fires, the parent still sees the old status and both the
    // saved approval fields and the computed overallStatus are wrong until
    // a second save (same class of bug already worked around for the
    // default "Pending" push above).
    if (isNonEmptyVariable(updateForm)) {
      updateForm({ ...buildAdministratorData(), administratorStatus: e });
    }
  };

  const hasAdminPermission = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.arbitary,
    LABELS_CAPITAL.VIEW,
    Arbitrary_Names.AdminView
  );

  // Once this section is Approved it's locked for editing, same rule at
  // every level (main/inmate/supplement) since they all render this same
  // component - Unlock (resets status back to Pending) is the only way
  // back in, rather than parentFieldEnable/Modify mode alone.
  const isApproved = status?.trim?.() === "Approved";

  return (
    <>
      <StyledSectionHeading marginBottom="15px">
        <span>Administrator</span>
      </StyledSectionHeading>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.JAIL_INCIDENTS_APPROVAL}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled && parentFieldEnable && hasAdminPermission}
      >
        <Form
          form={form}
          onFieldsChange={() => {
            if (isNonEmptyVariable(updateForm)) dataModify();
          }}
        >
          <StyledGridRow gutter={[16]}>
            <StyledGridCol lg={12}>
              <SecureFormItem
                fieldName={
                  isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorStaffId"]
                    : "administratorStaffId"
                }
                formItemProps={{
                  label: "Administrator Name",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorStaffId"]
                    : "administratorStaffId",
                }}
                overrideDisabled={isApproved}
              >
                {hasAdminPermission ? (
                  <Select
                    id="administratorStaffId"
                    options={getOptionsFromRefList("", true, true, true)}
                    placeholder="Select Administrator"
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                  />
                ) : (
                  <ApprovalReadOnlyValue
                    options={getOptionsFromRefList("", true, true, true)}
                  />
                )}
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName={
                  isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorStatus"]
                    : "administratorStatus"
                }
                formItemProps={{
                  label: "Status",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorStatus"]
                    : "administratorStatus",
                }}
                overrideDisabled={isApproved}
              >
                {hasAdminPermission ? (
                  <Select
                    id="AdministratorStatus"
                    options={allOptions}
                    placeholder="Select Status"
                    onChange={changeStatus}
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                  />
                ) : (
                  <ApprovalReadOnlyValue options={allOptions} />
                )}
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName={
                  isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorDateTime"]
                    : "administratorDateTime"
                }
                formItemProps={{
                  label: "Date & Time",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "administratorDateTime"]
                    : "administratorDateTime",
                }}
                overrideDisabled={isApproved}
              >
                <CommonDatePicker
                  id="reportingDateTime"
                  format="MM/DD/YYYY HH:mm"
                  disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                />
              </SecureFormItem>
            </StyledGridCol>

            {status === "Rejected" && (
              <StyledGridCol xs={24} lg={24}>
                <SecureFormItem
                  fieldName={
                    isNonEmptyVariable(fromkey)
                      ? [fromkey, 0, "administratorRejectReason"]
                      : "administratorRejectReason"
                  }
                  formItemProps={{
                    label: "Reject Reason",
                    name: isNonEmptyVariable(fromkey)
                      ? [fromkey, 0, "administratorRejectReason"]
                      : "administratorRejectReason",
                  }}
                >
                  <TextField
                    inputType="textarea"
                    rows="2"
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                  />
                </SecureFormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>
    </>
  );
});

Administrator.displayName = "Administrator";

export default Administrator;
